import React, { useState } from "react";
import cx from "classnames";
import { ColorResult } from "react-color";
import { IKImage } from "imagekitio-react";

import SwitchButton from "src/components/switchButton";
import {
  Appearance,
} from "src/types/experiences/storyboardSettings";
import {
  MediaResponse,
  SolidColor,
} from "src/types/experiences/storyboardSettings/primitives";

import Card from "../card";
import { useDraft } from "../useDraft";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import SegmentButton from "src/components/segmentButton";
import { ColorPicker } from "src/components/color";
import { EditSocialImage } from "../socialImageCard/editBanner";
import BrowserTabBar from "src/pages/experiences/storyboardSettings/components/browserTabBar";
import { isAbsoluteUrl, colorToCss, rgbToSolidColor, withBackgroundColor, resolveMediaUrl } from "src/pages/experiences/utils/cleverstorySitesettings";
import { DEFAULT_BACKGROUND, OVERLAY_OPTIONS } from "src/pages/experiences/constants/storyboard";
import shared from "../siteSettings.module.scss";
import styles from "./appearanceCard.module.scss";

const AppearanceCard: React.FC = () => {
  const { draft, setField } = useDraft();
  const appearance: Appearance = draft?.appearance ?? {
    disableDownload: false,
  };
  const editable = canEdit(draft?.role);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const update = (partial: Partial<Appearance>) => {
    if (!editable) return;
    setField("appearance", { ...appearance, ...partial } as Appearance);
  };

  const handleEditClose = (
    value: MediaResponse | null,
    position: string,
  ) => {
    setIsEditOpen(false);
    if (!value) return;
    const existing = appearance.background ?? DEFAULT_BACKGROUND;

    const mediaType =
      value.type ||
      (value.contentType?.startsWith("video/") ? "video" : "image");
    update({
      background: {
        ...existing,
        type: "image",
        media: { ...value, type: mediaType },
        position,
        opacity: existing.opacity > 1 ? existing.opacity : 100,
      },
    });
  };

  const bgColor = appearance.background?.color as SolidColor | null | undefined;
  const iconColor = appearance.iconColor;
  const bgMediaType =
    appearance.background?.media?.type ?? appearance.background?.type;

  const backgroundMediaType = appearance.background?.media
    ? (appearance.background.media.type ||
      appearance.background.type ||
      "image")
    : appearance.background?.type;

  const favIconUrl =
    typeof draft?.favIcon === "string"
      ? (draft.favIcon as unknown as string)
      : (draft?.favIcon?.favIcon196x196 ??
        draft?.favIcon?.favIcon96x96 ??
        draft?.favIcon?.favIcon32x32 ??
        null);

  return (
    <Card
      title="Appearance"
      description="Give your visitors an enhanced experience with our viewer customization"
    >
      <div className={styles.appearanceContainer}>
        <div
          className={styles.interactiveViewer}
          style={{ background: colorToCss(bgColor) }}
        >
          {appearance.background?.media?.url &&
            backgroundMediaType === "video" && (
              <div className={styles.backgroundImage}>
                <video
                  src={resolveMediaUrl(appearance.background.media.url)}
                  className={styles.backgroundImage}
                  style={{ objectPosition: appearance.background.position }}
                  autoPlay
                  muted
                  loop
                  playsInline
                />
              </div>
            )}
          {appearance.background?.media?.url &&
            backgroundMediaType === "image" && (
              <div className={styles.backgroundImage}>
                {isAbsoluteUrl(appearance.background.media.url) ? (
                  <img
                    src={appearance.background.media.url}
                    alt=""
                    className={styles.backgroundImage}
                  />
                ) : (
                  <IKImage
                    path={appearance.background.media.url}
                    alt=""
                    className={styles.backgroundImage}
                  />
                )}
              </div>
            )}
          <div
            className={cx(styles.overlaySection, {
              [styles.lightOverlay]: appearance.overlay === "light",
              [styles.darkOverlay]: appearance.overlay === "dark",
            })}
          />
          <div className={styles.headerContainer}>
            <BrowserTabBar
              icon={favIconUrl ?? ""}
              tabName={draft?.name ?? ""}
              tabURL={draft?.domain?.url ?? ""}
              className={styles.headerTabBar}
              classNames={{ emptyArea: styles.hiddenEmptyArea }}
            />
          </div>
          <div className={styles.iconsContainer}>
            <div className={styles.menuAction}>
              <i
                style={{ color: colorToCss(iconColor) }}
                className={cx("paperflite-menu", styles.menuIcon)}
              />
            </div>
            <div className={styles.actionContainer}>
              {!appearance.disableDownload && (
                <i
                  style={{ color: colorToCss(iconColor) }}
                  className={cx("paperflite-down-arrow", styles.actionIcons)}
                />
              )}
              <i
                style={{ color: colorToCss(iconColor) }}
                className={cx("paperflite-search", styles.actionIcons)}
              />
              <i
                style={{ color: colorToCss(iconColor) }}
                className={cx("paperflite-expand", styles.actionIcons)}
              />
            </div>
          </div>
          <div className={styles.contentSection}>
            <img
              src="/images/experiences/appearanceContent.png"
              alt=""
              className={styles.contentImage}
            />
          </div>
        </div>
      </div>

      <div className={cx(styles.footerRow, !editable && styles.disabled)}>
        <div className={styles.controls}>
          <div className={styles.control}>
            <button
              type="button"
              className={styles.uploadIcon}
              aria-label="Upload media"
              disabled={!editable}
              onClick={() => setIsEditOpen(true)}
            >
              <i className="paperflite-upload" />
            </button>
            <div className={styles.controlText}>
              <div className={styles.controlTitle}>Upload Media</div>
              <div className={styles.controlSub}>Choose from media library</div>
            </div>
          </div>
        </div>

        <div className={styles.controlDivider} />

        <div className={styles.control}>
          <ColorPicker
            color={colorToCss(bgColor)}
            onChange={(color) => {
              const { rgb } = color as unknown as ColorResult;
              update({
                background: withBackgroundColor(
                  appearance.background,
                  rgbToSolidColor(rgb),
                ),
              });
            }}
            onClose={() => {
              update({
                background: withBackgroundColor(appearance.background, bgColor),
              });
            }}
          >
            <span
              className={styles.swatch}
              style={{ background: colorToCss(bgColor) }}
            />
          </ColorPicker>
          <div className={styles.controlText}>
            <div className={styles.controlTitle}>Background Color</div>
            <div className={styles.controlSub}>{colorToCss(bgColor)}</div>
          </div>
        </div>

        <div className={styles.controlDivider} />

        <div className={styles.control}>
          <ColorPicker
            color={colorToCss(iconColor)}
            onChange={(color) => {
              const { rgb } = color as unknown as ColorResult;
              update({
                iconColor: rgbToSolidColor(rgb),
              });
            }}
            onClose={() => {
              update({
                iconColor: iconColor,
              });
            }}
          >
            <span
              className={styles.swatch}
              style={{ background: colorToCss(iconColor) }}
            />
          </ColorPicker>
          <div className={styles.controlText}>
            <div className={styles.controlTitle}>Icons Color</div>
            <div className={styles.controlSub}>{colorToCss(iconColor)}</div>
          </div>
        </div>

        <div className={styles.controlDivider} />

        <label className={styles.field}>
          <span className={shared.fieldLabel}>Overlay</span>
          <SegmentButton
            listOfSegments={OVERLAY_OPTIONS.map((option) => option.label)}
            selected={
              OVERLAY_OPTIONS.find((o) => o.value === appearance.overlay)?.label
            }
            onChange={(label) => {
              const match = OVERLAY_OPTIONS.find((o) => o.label === label);
              if (match) update({ overlay: match.value });
            }}
          />
        </label>

        <div className={styles.controlDivider} />

        <SwitchButton
          id="displayDownloadIcon"
          label="Download Icon"
          className={cx(shared.switch, styles.disableDownloadSwitch, {
            [shared.active]: !appearance.disableDownload,
          })}
          disabled={!editable}
          checked={!appearance.disableDownload}
          handleCheck={() =>
            update({ disableDownload: !appearance.disableDownload })
          }
        />
      </div>

      {isEditOpen && (
        <EditSocialImage
          image={appearance.background?.media}
          position={appearance.background?.position}
          isOpen={isEditOpen}
          allowVideo
          onClose={handleEditClose}
        />
      )}
    </Card>
  );
};

export default AppearanceCard;
